import {
    asyncOperationCreateIdleState,
    asyncOperationCreatePendingState,
    asyncOperationCreateRejectedState, asyncOperationIsIdle, asyncOperationIsRejected,
    IAsyncOperationState
} from '../async-state'
import {ObservableValue} from '../observable-value/observable-value'
import {tsTypeAssert} from "../ts-type-utils/ts-type-utils";
import {EventEmitter} from "../event-emitter";

export class AsyncTasksScheduler<TASK> {

    private taskSchedulingHandlingFunction;
    private taskExecutionFunction;
    private onTaskRejected;

    private tasksQueue: TASK[] = [];
    private currentTask?: TASK = undefined;

    public readonly observableCurrentTask = new ObservableValue<TASK | undefined>(undefined);
    public readonly observableAsyncOperationState = new ObservableValue<IAsyncOperationState>(asyncOperationCreateIdleState())

    constructor(options: AsyncTasksScheduler.Options<TASK>) {
        this.taskSchedulingHandlingFunction = options.handleTaskScheduling;
        this.taskExecutionFunction = options.executeTask;
        this.onTaskRejected = options.onTaskRejected;
    }

    scheduleTask (task: TASK) : TASK {

        const currentTask = this.currentTask;

        this.taskSchedulingHandlingFunction(task, tsTypeAssert<AsyncTasksScheduler.SchedulingController<TASK>>({
            currentTask: currentTask,
            abortCurrentTask: (abortingFunc) => {
                if (currentTask && abortingFunc) {
                    abortingFunc(currentTask);
                }

                this.currentTask = undefined;
            },
            enqueueTask: (task) => {
                this.tasksQueue.push(task);
            },
            clearPendingTasks: () => {
                this.tasksQueue = [];
            }
        }))

        if (this.currentTask === undefined) {
            this.executeNextTask();
        }

        return task;
    }

    /**
     * Returns true if there are tasks waiting in the queue to be executed.
     * Does not include the currently executing task.
     */
    public hasPendingTasks(): boolean {
        return this.tasksQueue.length > 0;
    }

    public async waitForAllTasksToComplete () {
        if (asyncOperationIsIdle(this.observableAsyncOperationState.getValue())) {
            return;
        } else {
            return new Promise<void>((resolve, reject) => {
                const binding = EventEmitter.on(this.observableAsyncOperationState.eventUpdated, () => {

                    const asyncOperationState = this.observableAsyncOperationState.getValue();

                    if (asyncOperationIsIdle(asyncOperationState)) {
                        binding.dispose();
                        resolve();
                    } else if (asyncOperationIsRejected(asyncOperationState)) {
                        binding.dispose();
                        reject(asyncOperationState.reason);
                    }
                })
            })
        }
    }

    private executeNextTask (lastTaskFailureReason?: any) {

        const nextTask = this.currentTask = this.tasksQueue.shift();
        this.observableCurrentTask.setValue(nextTask);

        if (nextTask) {

            const promise = this.taskExecutionFunction(nextTask)
                .then(() => {
                    this.executeNextTask();
                })
                .catch((error: any) => {

                    this.onTaskRejected?.(nextTask, error);

                    this.executeNextTask(error);
                })

            this.observableAsyncOperationState.setValue(asyncOperationCreatePendingState(promise))
        } else {

            if (lastTaskFailureReason) {
                this.observableAsyncOperationState.setValue(asyncOperationCreateRejectedState(lastTaskFailureReason))
            } else {
                this.observableAsyncOperationState.setValue(asyncOperationCreateIdleState())
            }
        }
    }
}

export namespace AsyncTasksScheduler {

    export interface SchedulingController<TASK> {
        currentTask : TASK | undefined;
        clearPendingTasks () : void;
        abortCurrentTask (abortingFunc?: (currentTask: TASK) => void) : void;
        enqueueTask (task: TASK) : void;
    }

    export interface Options<TASK> {
        handleTaskScheduling: (task: TASK, schedulingController: SchedulingController<TASK>) => void;

        executeTask: (task: TASK) => Promise<void>;

        onTaskRejected?: (task: TASK, reason: any) => void;
    }

}